import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import MetaTags from '@/components/SEO/MetaTags';
import BrandLogo from '@/components/brand/BrandLogo';
import { apiUrl } from '@/config/api';
import { getAuthToken } from '@/utils/auth';

const DRAW_PATH = '/lifetime-draw';
const PAGE_URL = 'https://www.getbyonco.com/lifetime-draw';

function authHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = getAuthToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

async function readJson(response) {
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = data?.detail;
    const message = typeof detail === 'string' && detail.trim()
      ? detail
      : 'Something went wrong. Please try again.';
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  return data;
}

function pad(value) {
  return String(value).padStart(2, '0');
}

function useCountdown(iso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);
  return useMemo(() => {
    const target = iso ? new Date(iso).getTime() : NaN;
    const diff = Number.isFinite(target) ? Math.max(0, target - now) : 0;
    const totalSeconds = Math.floor(diff / 1000);
    return {
      done: diff <= 0,
      days: Math.floor(totalSeconds / 86400),
      hours: pad(Math.floor((totalSeconds % 86400) / 3600)),
      minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
      seconds: pad(totalSeconds % 60),
    };
  }, [iso, now]);
}

function CountCell({ value, label }) {
  return (
    <div className="rounded-2xl bg-white/10 border border-white/15 px-3 py-3 sm:px-4 sm:py-4 text-center min-w-[4.5rem]">
      <div className="text-2xl sm:text-3xl font-semibold tabular-nums tracking-tight">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.14em] text-white/70 mt-1">{label}</div>
    </div>
  );
}

export default function LifetimeDrawPage() {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [snapshot, setSnapshot] = useState(null);
  const [mine, setMine] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [copied, setCopied] = useState('');

  const countdown = useCountdown(snapshot?.draw_at);

  const load = async () => {
    setLoadError('');
    try {
      const statusRes = await fetch(apiUrl('/api/draw/lifetime'), { credentials: 'include' });
      const status = await readJson(statusRes);
      setSnapshot(status);
      if (!isAuthenticated) {
        setMine(null);
        return;
      }
      const meRes = await fetch(apiUrl('/api/draw/lifetime/me'), {
        credentials: 'include',
        headers: authHeaders(),
      });
      if (meRes.status === 401) {
        setMine(null);
        return;
      }
      const entry = await readJson(meRes);
      setMine(entry);
      if (entry?.code) setCodeInput(entry.code);
    } catch (error) {
      setLoadError('The live count is unavailable right now. You can still subscribe, and your code will be here once payment is confirmed.');
    }
  };

  useEffect(() => {
    if (authLoading) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, user?.id]);

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(label);
      window.setTimeout(() => setCopied(''), 2000);
    } catch {
      setCopied('');
    }
  };

  const submitEntry = async (event) => {
    event.preventDefault();
    if (!isAuthenticated) {
      navigate(`/authentication?redirect=${encodeURIComponent(DRAW_PATH)}`);
      return;
    }
    setBusy(true);
    setNotice('');
    try {
      const response = await fetch(apiUrl('/api/draw/lifetime/enter'), {
        method: 'POST',
        credentials: 'include',
        headers: authHeaders(),
        body: JSON.stringify({ code: codeInput }),
      });
      const entry = await readJson(response);
      setMine(entry);
      setNotice(entry.is_winner
        ? 'Your code was selected. ByOnco PRO is yours for life.'
        : 'You are in the draw.');
      const statusRes = await fetch(apiUrl('/api/draw/lifetime'), { credentials: 'include' });
      setSnapshot(await readJson(statusRes));
    } catch (error) {
      if (error.status === 401) {
        navigate(`/authentication?redirect=${encodeURIComponent(DRAW_PATH)}`);
        return;
      }
      setNotice(error.message);
    } finally {
      setBusy(false);
    }
  };

  const places = snapshot?.codes_remaining ?? 100;
  const drawLabel = snapshot?.draw_at_label || '1 January 2027, 12:00 noon EST';
  const status = mine?.status;
  const hasCode = Boolean(mine?.code);
  const inDraw = Boolean(mine?.entered);
  const soldOut = Boolean(snapshot?.pool_ready) && places === 0 && !hasCode;

  return (
    <div className="min-h-screen bg-cream-paper text-charcoal">
      <MetaTags
        title="Lifetime draw for 10 ByOnco members"
        description="The first 100 monthly subscribers each receive one code. On 1 January 2027, ten codes are drawn. Those ten people keep ByOnco PRO for life."
        path={DRAW_PATH}
      />

      <header className="max-w-5xl mx-auto px-4 sm:px-6 pt-8 flex items-center justify-between gap-4">
        <BrandLogo size="md" />
        <Link to="/subscribe?plan=monthly&redirect=%2Flifetime-draw" className="text-sm text-forest-ink underline underline-offset-4">
          Monthly plan
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-20">
        <section className="mt-8 sm:mt-12 rounded-[28px] bg-forest-ink text-white px-5 py-8 sm:px-10 sm:py-12">
          <p className="text-[11px] uppercase tracking-[0.18em] text-white/70">Lifetime draw</p>
          <h1 className="mt-3 text-3xl sm:text-5xl font-light tracking-tight leading-tight max-w-3xl">
            Ten people will keep ByOnco for life.
          </h1>
          <p className="mt-4 text-base sm:text-lg text-white/85 max-w-2xl leading-relaxed">
            The first 100 people who subscribe to the monthly plan each receive one code.
            Enter it here. On {drawLabel}, ten codes are drawn. Those ten memberships become lifetime access, with nothing more to pay.
          </p>

          <div className="mt-8 flex flex-wrap gap-3" aria-live="polite">
            {countdown.done ? (
              <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-4 text-sm">
                The draw date has arrived.
              </div>
            ) : (
              <>
                <CountCell value={countdown.days} label="Days" />
                <CountCell value={countdown.hours} label="Hours" />
                <CountCell value={countdown.minutes} label="Minutes" />
                <CountCell value={countdown.seconds} label="Seconds" />
              </>
            )}
          </div>

          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
            <Link
              to="/subscribe?plan=monthly&redirect=%2Flifetime-draw"
              className="inline-flex items-center justify-center rounded-full bg-white text-forest-ink px-6 py-3 text-sm font-medium hover:bg-keylime-wash"
            >
              Subscribe monthly, $19.99
            </Link>
            <p className="text-sm text-white/75">
              {snapshot?.drawn
                ? `${snapshot.winners_selected || 0} lifetime memberships have been selected.`
                : `${places} of 100 codes still open.`}
            </p>
          </div>
        </section>

        <section id="your-code" className="mt-8 rounded-3xl border border-border-mist bg-white p-5 sm:p-8">
          <h2 className="text-2xl font-light text-forest-ink tracking-tight">Your code</h2>
          <p className="mt-2 text-sm text-charcoal/75 leading-relaxed max-w-2xl">
            A code appears on this page after the monthly payment is confirmed. One code per account. Enter it before the draw.
          </p>

          {loadError ? (
            <p className="mt-6 text-sm text-red-800" role="alert">{loadError}</p>
          ) : null}

          {!authLoading && !isAuthenticated ? (
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link
                to={`/authentication?redirect=${encodeURIComponent(DRAW_PATH)}`}
                className="inline-flex items-center justify-center rounded-full bg-forest-ink text-white px-5 py-3 text-sm"
              >
                Sign in to see your code
              </Link>
              <Link
                to="/subscribe?plan=monthly&redirect=%2Flifetime-draw"
                className="inline-flex items-center justify-center rounded-full border border-forest-ink text-forest-ink px-5 py-3 text-sm"
              >
                I need to subscribe first
              </Link>
            </div>
          ) : null}

          {isAuthenticated && !hasCode && !soldOut ? (
            <div className="mt-6 rounded-2xl bg-keylime-wash/60 border border-border-mist p-5">
              <p className="text-sm leading-relaxed">
                This account does not have a draw code yet. Subscribe to the monthly plan and come back to this page. The code is assigned when the payment is confirmed.
              </p>
              <Link
                to="/subscribe?plan=monthly&redirect=%2Flifetime-draw"
                className="inline-flex mt-4 rounded-full bg-forest-ink text-white px-5 py-3 text-sm"
              >
                Get the monthly plan
              </Link>
            </div>
          ) : null}

          {soldOut ? (
            <p className="mt-6 text-sm leading-relaxed">
              All 100 codes have been claimed. The monthly plan is still open. This draw is limited to those 100 codes.
            </p>
          ) : null}

          {hasCode ? (
            <div className="mt-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <p className="font-mono text-2xl sm:text-3xl tracking-[0.18em] text-forest-ink" data-testid="draw-code">
                  {mine.code}
                </p>
                <button
                  type="button"
                  onClick={() => copyText(mine.code, 'code')}
                  className="text-sm underline underline-offset-4 text-forest-ink w-fit"
                >
                  {copied === 'code' ? 'Copied' : 'Copy code'}
                </button>
              </div>
              {status === 'winner' ? (
                <p className="mt-4 text-sm leading-relaxed text-forest-ink">
                  Your code was selected. ByOnco PRO is now on this account for life, with nothing more to pay.
                </p>
              ) : null}
              {status === 'not_selected' ? (
                <p className="mt-4 text-sm leading-relaxed">
                  The draw has been held. This code was not selected. Your monthly plan continues as it is.
                </p>
              ) : null}
              {status === 'void' ? (
                <p className="mt-4 text-sm leading-relaxed">
                  This entry was closed because the monthly plan was not active on the draw date.
                </p>
              ) : null}
              {inDraw && status === 'entered' ? (
                <p className="mt-4 text-sm leading-relaxed">
                  You are in the draw. Keep the monthly plan active through {drawLabel}. We will show the result on this page.
                </p>
              ) : null}
            </div>
          ) : null}

          {isAuthenticated && !snapshot?.drawn && !inDraw && status !== 'void' ? (
            <form onSubmit={submitEntry} className="mt-8 max-w-xl">
              <label htmlFor="draw-code-input" className="text-sm text-charcoal/80">
                Enter your code
              </label>
              <div className="mt-2 flex flex-col sm:flex-row gap-3">
                <input
                  id="draw-code-input"
                  value={codeInput}
                  onChange={(event) => setCodeInput(event.target.value.toUpperCase())}
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="BYO-XXXX-XXXX"
                  className="flex-1 rounded-2xl border border-border-mist bg-cream-paper px-4 py-3 font-mono tracking-wider text-forest-ink"
                />
                <button
                  type="submit"
                  disabled={busy || snapshot?.drawn}
                  className="rounded-full bg-forest-ink text-white px-6 py-3 text-sm disabled:opacity-60"
                >
                  {busy ? 'Entering…' : inDraw ? 'Entry saved' : 'Enter the draw'}
                </button>
              </div>
              {notice ? <p className="mt-3 text-sm" role="status">{notice}</p> : null}
            </form>
          ) : null}
        </section>

        <section className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            ['1', 'Subscribe', 'Pay for the ByOnco PRO monthly plan. The first 100 payments each receive one code.'],
            ['2', 'Take your code', 'Sign in on this page after payment. Your code is shown once it is on your account.'],
            ['3', 'Enter by 1 January 2027', 'Submit the code here. Ten active entries are drawn at 12:00 noon EST.'],
          ].map(([step, title, body]) => (
            <article key={step} className="rounded-3xl border border-border-mist bg-white p-5">
              <p className="text-xs uppercase tracking-[0.16em] text-forest-ink/70">Step {step}</p>
              <h3 className="mt-2 text-lg text-forest-ink">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-charcoal/80">{body}</p>
            </article>
          ))}
        </section>

        <section className="mt-8 rounded-3xl border border-border-mist bg-keylime-wash/40 p-5 sm:p-8">
          <h2 className="text-2xl font-light text-forest-ink tracking-tight">Rules</h2>
          <ul className="mt-4 space-y-3 text-sm leading-relaxed text-charcoal/85 max-w-3xl">
            <li>The prize is ByOnco PRO membership for life: the companion and navigation service on this account. Clinical decisions stay with the clinicians already caring for you. There is no cash award.</li>
            <li>One entry per account. The code is issued only after a monthly plan payment is confirmed, while codes remain.</li>
            <li>The monthly plan must still be active when you enter, and on {drawLabel}.</li>
            <li>Ten codes are drawn. If fewer than ten people have entered, every eligible entry is selected.</li>
            <li>People who are not selected keep the monthly plan they paid for.</li>
            <li>ByOnco team accounts are not entered.</li>
            <li>Results are shown on this page to the signed-in member. Names are kept private.</li>
          </ul>
          <p className="mt-5 text-xs text-charcoal/60">
            Questions: <a className="underline underline-offset-2" href="mailto:care@getbyonco.com">care@getbyonco.com</a>
          </p>
        </section>

        <section className="mt-8 rounded-3xl border border-border-mist bg-white p-5 sm:p-8">
          <h2 className="text-lg text-forest-ink">Share the draw</h2>
          <p className="mt-3 text-sm leading-relaxed text-charcoal/80 max-w-2xl">
            ByOnco is giving lifetime access to 10 people. Subscribe to the monthly plan, collect your code, and enter before 1 January 2027. {PAGE_URL}
          </p>
          <button
            type="button"
            onClick={() => copyText(
              `ByOnco is giving lifetime access to 10 people. Subscribe to the monthly plan, collect your code, and enter before 1 January 2027. ${PAGE_URL}`,
              'share',
            )}
            className="mt-4 text-sm underline underline-offset-4 text-forest-ink"
          >
            {copied === 'share' ? 'Copied' : 'Copy this line'}
          </button>
        </section>
      </main>
    </div>
  );
}
